import analytics from '@/shared-utils/analytics'
import config from 'isomorphic-config'
import getAuthorizationToken from '@/shared-utils/get-authorization-token'
import isAbortSignalTimeoutSupported from '@/shared-utils/is-abortSignal-supported'
import getCookieByName from '@/shared-utils/get-cookie-by-name'
import type { FavoriteHotel } from '@/types'
import { jsonContentTypeHeader } from '../server/constants'

export default async function toggleFavoriteHotel(
  type: 'add' | 'remove',
  args: FavoriteHotel
) {
  const { cguid, hotelId, cityId, appName, appVersion } = args
  const authToken = getCookieByName('dmc') || ''
  const token = getAuthorizationToken()

  const mutationName =
    type === 'add' ? 'AddFavoriteHotel' : 'RemoveFavoriteHotel'
  const query = `mutation ${mutationName}($cguid: ID!, $favoriteHotelInput: FavoriteHotelInput!) {
    ${type}FavoriteHotel(cguid: $cguid, favoriteHotelInput: $favoriteHotelInput) {
      ... on FavoriteHotel {
        hotelId
      }
      ... on FavoriteHotelError {
        favoriteHotelError
      }
    }
  }`

  const variables = {
    cguid,
    favoriteHotelInput: {
      hotelId,
      cityId
    }
  }

  const { url, timeout } = config.client['pcln-graph']
  const requestUrl = `${url}?gqlOp=${type}FavoriteHotel`

  const options = {
    method: 'POST',
    signal: isAbortSignalTimeoutSupported()
      ? AbortSignal.timeout(timeout)
      : undefined,
    headers: {
      ...jsonContentTypeHeader,
      Authorization: token,
      authToken,
      'apollographql-client-name': appName,
      'apollographql-client-version': appVersion
    },
    body: JSON.stringify({ query, variables })
  }

  try {
    const response = await fetch(requestUrl, options)
    const payload = await response.json()

    if (!response.ok || payload.errors) {
      const errorMessage = payload.errors?.[0]?.message || response.statusText
      analytics.logError({
        message: `${type}FavoriteHotel failed`,
        errorMessage,
        status: response.status,
        url: requestUrl,
        payload: JSON.stringify(payload)
      })
      throw new Error(errorMessage)
    }

    return payload.data?.[`${type}FavoriteHotel`]
  } catch (error) {
    analytics.logError({
      message: `Unexpected error in ${type}FavoriteHotel`,
      error: JSON.stringify(error),
      service: `${type}FavoriteHotel`
    })
    throw error
  }
}
